import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { getShopProducts } from "../../../network/service/ShopService";

const PAGE_SIZE = 20;

/**
 * Product cards for a store and sub-category, with filter, sort and search
 * done on the server and pages appended for infinite scroll. A newer query
 * cancels the request in flight, so results never arrive out of order.
 */
export function useShopProducts({ storeId, subCategoryId, filter, sort, search }) {
  const [state, setState] = useState({
    items: [],
    page: 0,
    totalPages: 0,
    total: 0,
    filters: [],
    subCategory: null,
    status: "loading", // loading | ready | error
    loadingMore: false,
    error: null,
  });
  const controller = useRef(null);

  const fetchPage = useCallback(
    async (page) => {
      controller.current?.abort();
      const abort = new AbortController();
      controller.current = abort;

      setState((s) => (page === 1 ? { ...s, status: "loading", error: null } : { ...s, loadingMore: true }));
      try {
        const data = await getShopProducts(
          storeId,
          { subCategoryId, filter, sort, search: search || undefined, page, limit: PAGE_SIZE },
          abort.signal,
        );
        setState((s) => ({
          items: page === 1 ? data.items : [...s.items, ...data.items],
          page: data.page,
          totalPages: data.totalPages,
          total: data.total,
          filters: data.filters,
          subCategory: data.subCategory,
          status: "ready",
          loadingMore: false,
          error: null,
        }));
      } catch (error) {
        if (axios.isCancel(error)) return;
        setState((s) =>
          page === 1 ? { ...s, status: "error", error, loadingMore: false } : { ...s, loadingMore: false, error },
        );
      }
    },
    [storeId, subCategoryId, filter, sort, search],
  );

  useEffect(() => {
    if (!storeId) return undefined;
    fetchPage(1);
    return () => controller.current?.abort();
  }, [storeId, fetchPage]);

  const hasMore = state.page < state.totalPages;
  const loadMore = useCallback(() => {
    if (hasMore && !state.loadingMore && state.status === "ready") fetchPage(state.page + 1);
  }, [hasMore, state.loadingMore, state.status, state.page, fetchPage]);

  return { ...state, hasMore, loadMore, retry: () => fetchPage(1) };
}
